import {
  Alert,
  Button,
  Col,
  Collapse,
  Divider,
  Flex,
  Form,
  FormListFieldData,
  Input,
  InputNumber,
  InputNumberProps,
  message,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Tooltip,
  Typography,
} from "antd";
import { useWatch } from "antd/es/form/Form";
import React, { ReactNode, useEffect, useState } from "react";
import { formatMoney, getDebtBasedWeeks, parseMoney, Safe, toCapitalCase, toTitleCase } from "utils/functions";
import {
  calculateRevisedWeeks,
  calculateWeeklyCommissionAmount,
  calculateWeeklyInitialPayment,
  getTotalInitialPayment,
} from "./functions-old";

import {
  ResolutionType,
  StrategyFormat,
  PaymentType,
  TargetWeeklyCalculationStrategy,
  ProgramSplitFormat,
  CreditorSplitFormat,
  CreditorPayFormat,
  StrategyConfigV2,
  DisplayTimeline,
  ProgramSplit,
  CreditorSplit,
  InterestCalculationStrategy,
  StrategyRuleKeys,
  StrategyRules,
  ExtraFee,
  InitialPaymentPercentageOf,
  defaultStrategyValue,
} from "dtos/strategy.dto";
import {
  TbTargetArrow,
  TbSettingsAutomation,
  TbCalendarRepeat,
  TbMathFunction,
  TbTableOptions,
  TbReceiptTax,
  TbTrophy,
  TbFlagCheck,
  TbScale,
  TbArrowUp,
  TbArrowDown,
  TbColumns,
  TbInfoCircle,
  TbUsersGroup,
  TbEdit,
  TbChevronsRight,
  TbPercentage,
  TbEye,
  TbFileText,
  TbTrash,
  TbPlus,
  TbFilter,
} from "react-icons/tb";
import { useGenerateStrategy } from "utils/hooks/useGenerateStrategy";
import { ApplicationDto } from "dtos/application.dto";
import { getStrategyContext } from "utils/core/strategy.util";
import WeeklyPaymentTimeline from "./WeeklyPaymentTimeline";
import PaymentBreakdown from "./PaymentBreakdown";
import ModifyParameters, { CustomParameters } from "pages/Organisation/CustomizeParams";
import { defaultHeaders, LedgerTable } from "components/StrategyPreview/Ledger";
import { ApiService } from "api/api.service";
const { Panel } = Collapse;

const Section = ({
  title,
  children,
  bordered,
  extra,
}: {
  title: string;
  children: ReactNode;
  bordered?: boolean | "bottom" | "top";
  extra?: ReactNode;
}) => {
  return (
    <div className={`px-1 pt-4 pb-2 ${bordered ? "border-0 border-t border-solid border-gray-300/80" : ""}`}>
      <Flex className="justify-between">
        <Typography.Title level={5} className="!mb-4 uppercase !text-sm !text-gray-500/90">
          {title}
        </Typography.Title>
        {extra}
      </Flex>
      <div className="px-2">{children}</div>
    </div>
  );
};

const StatCard = ({ label, text, subtext }: { label: string; text: string; subtext?: string }) => {
  return (
    <Flex vertical className="w-full min-h-16">
      <Typography.Text className="!text-sm">{label}</Typography.Text>
      <Typography.Title level={4} className="!m-0">
        {text}
      </Typography.Title>
      {subtext?.length && <span className="!text-xs !text-gray-500">{subtext}</span>}
    </Flex>
  );
};

const EditableStatCard = ({
  label,
  text,
  onChange,
  min,
  subtext,
  ...props
}: Omit<InputNumberProps, "onChange"> & {
  label: string;
  text?: number;
  subtext?: string;
  onChange?: (val: number | string | null) => unknown;
}) => {
  const [localValue, setLocalValue] = useState<number | string | null>(text ?? null);
  const isFocused = React.useRef(false);
  useEffect(() => {
    if (!isFocused.current) {
      setLocalValue(text ?? null);
    }
  }, [text]);

  const commit = (val: number | string | null) => {
    if (!onChange) return;
    if (!val && val !== 0) return onChange(null);
    const num = Number(val);
    if (Number.isNaN(num)) return onChange(null);
    if (typeof min === "number" && num < min) {
      setLocalValue(min);
      onChange(min);
      message.error(`Can not go below ${min}`);
    } else {
      onChange(val);
    }
  };

  return (
    <Flex vertical className="w-full min-h-16">
      <Typography.Text className="!text-sm flex items-center gap-1">
        {label}
        {
          <Tooltip title="Editable">
            <TbEdit />
          </Tooltip>
        }
      </Typography.Text>
      <InputNumber
        value={localValue}
        onChange={(val) => setLocalValue(val)}
        onFocus={() => {
          isFocused.current = true;
        }}
        onBlur={() => {
          isFocused.current = false;
          commit(localValue);
        }}
        onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
        controls={false}
        variant="borderless"
        className="text-xl font-semibold text-black !p-0 [&_.ant-input-number-input]:!p-0 [&_.ant-input-number-input]:!m-0 [&_.ant-input-number-input]:!h-auto w-full"
        {...props}
      />
      {subtext?.length && <span className="!text-xs lowercase !text-gray-500">{subtext}</span>}
    </Flex>
  );
};

const StrategyPanel = ({
  name,
  totalDebt,
  setDisabled,
  extraAmount,
  totalLawsuits,
  hasSplit,
  application,
}: {
  name: number;
  totalDebt: number;
  remove: (name: number) => void;
  activeKey: string | string[] | undefined;
  setActiveKey: React.Dispatch<React.SetStateAction<string | string[] | undefined>>;
  setDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  extraAmount: number;
  minWeeklyBudget: number;
  totalLawsuits: number;
  hasSplit: boolean;
  application: ApplicationDto;
}) => {
  const form = Form.useFormInstance();
  const splitFormat = useWatch(["strategies", name, "program_split", "format"]);
  const context = getStrategyContext(application, { weekly_budget: application?.requested_budget });
  const generator = useGenerateStrategy(context);

  const program_part: number = useWatch(["strategies", name, "program_part"], form) ?? 0;
  const success_part: number = useWatch(["strategies", name, "success_part"], form) ?? 0;
  const bias_amount: number = useWatch(["strategies", name, "bias_amount"], form);
  const initial_payment_charge: number = useWatch(["strategies", name, "initial_payment_charge"], form);

  const slug: string = useWatch(["strategies", name, "slug"], form);
  const key: string = useWatch(["strategies", name, "key"], form);
  const description: string = useWatch(["strategies", name, "description"], form);
  const resolution_type: ResolutionType = useWatch(["strategies", name, "resolution_type"], form);
  const format: StrategyFormat = useWatch(["strategies", name, "format"], form);
  const payment_type: PaymentType = useWatch(["strategies", name, "payment_type"], form);
  const interestStrategy =
    Form.useWatch(["strategies", name, "interest_calculation_strategy"], form) ??
    defaultStrategyValue.interest_calculation_strategy;
  const interest_amount =
    Form.useWatch(["strategies", name, "interest_amount"], form) ?? defaultStrategyValue.interest_amount;
  const program_fee: number = useWatch(["strategies", name, "program_fee"], form) ?? defaultStrategyValue.program_fee;
  const success_fee: number = useWatch(["strategies", name, "success_fee"], form) ?? defaultStrategyValue.success_fee;
  const initial_fee: number = useWatch(["strategies", name, "initial_fee"], form) ?? defaultStrategyValue.initial_fee;
  const initial_fee_base =
    useWatch(["strategies", name, "initial_fee_base"], form) ?? defaultStrategyValue.initial_fee_base;
  const initial_bias_amount: number = useWatch(["strategies", name, "initial_bias_amount"], form) ?? 0;
  const is_bias_exclusive =
    useWatch(["strategies", name, "is_bias_exclusive"], form) ?? defaultStrategyValue.is_bias_exclusive;
  const dynamic_weekly_pushback: number = useWatch(["strategies", name, "dynamic_weekly_pushback"], form) ?? 0;
  const fallback_display_timeline: number | undefined = useWatch(
    ["strategies", name, "fallback_display_timeline"],
    form,
  );
  const getOrganisationDetails = ApiService.organisation.useGetBySlug(application.organisation?.code);
  const pandaDocConfig = getOrganisationDetails.data?.pandadoc_mappings ?? [];
  const target_weekly_calculation_strategy: TargetWeeklyCalculationStrategy = useWatch(
    ["strategies", name, "target_weekly_calculation_strategy"],
    form,
  );
  const program_split: ProgramSplit = useWatch(["strategies", name, "program_split"], form);
  const display_timeline: DisplayTimeline[] | undefined = useWatch(["strategies", name, "display_timeline"], form);
  const creditor_split: CreditorSplit = useWatch(["strategies", name, "creditor_split"], form);
  const meta: Record<string, unknown> = useWatch(["strategies", name, "meta"], form);
  const base =
    useWatch(["strategies", name, "program_split", "meta", "base"], form) ??
    defaultStrategyValue.program_split.meta.base;
  const rules: StrategyRules[] | undefined = useWatch(["strategies", name, "rules"], form);
  const service_fee: ExtraFee.Config[] | undefined = useWatch(["strategies", name, "service_fee"], form);
  const legal_fee: ExtraFee.Config[] | undefined = useWatch(["strategies", name, "legal_fee"], form);

  const pbToggle = dynamic_weekly_pushback >= 0;
  const [customizeParameters, setCustomizeParameters] = useState<CustomParameters[]>([]);

  const pctProps = {
    step: 0.01,
    min: 0,
    max: 1,
    formatter: (value: any) => (value ? `${(value * 100).toFixed(0)}%` : "0%"),
    parser: (value: any) => (value ? Number.parseFloat(value.replace("%", "")) / 100 : 0),
    className: "w-full",
  };

  const getProgramSplitTitle = (str: ProgramSplitFormat) => {
    switch (str) {
      case ProgramSplitFormat.UNIFIED:
        return "Unified";
      case ProgramSplitFormat.EARLY_STOPPING_WEEKS:
        return "Weeks";
      case ProgramSplitFormat.EARLY_STOPPING_PERCENTAGE:
        return "Percentage";
      case ProgramSplitFormat.PERCENTAGE_OF_WEEKLY:
        return "Percentage";
      default:
        return "Unknown";
    }
  };

  useEffect(() => {
    const strategy = form.getFieldValue(["strategies", name]);
    const activeCustomParams: CustomParameters[] = [];
    if (strategy?.service_fee?.length) activeCustomParams.push("service_fee");
    if (strategy?.legal_fee?.length) activeCustomParams.push("legal_fee");

    if (activeCustomParams.length) setCustomizeParameters(activeCustomParams);
  }, []);

  useEffect(() => {
    const total = (program_part ?? 0) + (success_part ?? 0);

    form.setFieldsValue({
      strategies: {
        [name]: {
          net_commission: total,
        },
      },
    });
  }, [program_part, success_part]);

  useEffect(() => {
    form.setFieldsValue({
      strategies: {
        [name]: {
          initial_payment: getTotalInitialPayment(initial_payment_charge, totalDebt, extraAmount, bias_amount),
        },
      },
    });
  }, [initial_payment_charge, bias_amount]);

  const Preview = ({
    setMetaFields,
    ...config
  }: StrategyConfigV2 & {
    index: number;
    title: string;
    setMetaFields: (field: string, value: string | number | boolean | undefined) => unknown;
  }) => {
    const query = generator.asyncCompile(config);
    const [paymentTableShowMode, setPaymentTableShowMode] = useState<"detailed" | "compact">("compact");
    const [visibleHeaders, setVisibleHeaders] = useState(defaultHeaders);

    if (!config.format) return null;

    const guardRails = generator.getGuardRails({ ...config, format: StrategyFormat.CUSTOM });

    if (query.isPending && !query.data) {
      return (
        <div className="col-span-6 max-h-[85vh] overflow-y-auto h-fit border border-solid border-gray-200 rounded-md shadow px-3 flex items-center justify-center py-24">
          <Spin tip="Compiling strategy..." />
        </div>
      );
    }

    if (query.isError && !query.data) {
      return (
        <div className="col-span-6 max-h-[85vh] overflow-y-auto h-fit border border-solid border-gray-200 rounded-md shadow px-3 py-6">
          <Alert
            type="error"
            showIcon
            message="Couldn't compile strategy preview"
            description="Try adjusting the values above, or check your connection."
          />
        </div>
      );
    }

    const { strategy, payment_table, ledger } = query.data;
    const frequency = strategy?.payment_type === PaymentType.MONTHLY ? "month" : "week";

    return (
      <div className="col-span-6 max-h-[85vh] overflow-y-auto gap-4 h-fit border border-solid border-gray-200 rounded-md shadow px-3 relative">
        {(query.isFetching || query.isError) && (
          <div className="sticky top-0 z-10 -mx-3 mb-2">
            <Tooltip title={query.isError ? "Preview refresh failed, showing last result" : "Refreshing preview..."}>
              <Spin
                size="small"
                className={query.isError ? "opacity-0" : undefined}
                indicator={query.isError ? <TbInfoCircle className="text-red-500 text-base" /> : undefined}
              />
            </Tooltip>
          </div>
        )}
        <Section title="Business Metrics">
          <Flex gap={16} vertical>
            <Row gutter={12}>
              <Col span={12}>
                <EditableStatCard
                  label="Weekly Budget"
                  text={config.meta?.weekly_budget as number | undefined}
                  min={guardRails.min_custom_amount}
                  onChange={(val) => {
                    if (!val || val === "") setMetaFields("weekly_budget", undefined);
                    else setMetaFields("weekly_budget", Number.isNaN(val) ? undefined : Number(val));
                  }}
                  formatter={(val) => {
                    if (!val || (typeof val === "number" && val < 1) || (typeof val === "string" && Number(val) < 1))
                      return "None";
                    return formatMoney(Number(val));
                  }}
                  parser={(val) => {
                    if (!val || val === "None") return "";
                    return parseMoney(val) ?? "";
                  }}
                  subtext={`minimum value $${guardRails.min_custom_amount}`}
                />
              </Col>
              <Col span={12}>
                <StatCard
                  label="Current Payment"
                  text={
                    application?.summary?.weekly_payments ? formatMoney(application.summary.weekly_payments) : "None"
                  }
                  subtext="per week"
                />
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}>
                <StatCard label="Total Lawsuits" text={String(application?.total_lawsuits || 0)} />
              </Col>
              <Col span={12}>
                <StatCard
                  label="Total Loans"
                  text={String(application?.total_loans || application?.loans?.length || 0)}
                />
              </Col>
            </Row>
          </Flex>
        </Section>
        <Section bordered title="Strategy Overview">
          <Flex gap={16} vertical>
            <Row gutter={12}>
              <Col span={12}>
                <StatCard
                  label="Payable Amount"
                  text={formatMoney(strategy?.common.total_payable, true)}
                  subtext={`${formatMoney(strategy?.common.weekly_payments ? strategy.common.weekly_payments * (frequency === "month" ? 4 : 1) : undefined, false)} / ${frequency}`}
                />
              </Col>
              <Col span={12}>
                <StatCard
                  label="Initial Payment"
                  text={formatMoney(strategy?.common.initial_payment, true)}
                  subtext={
                    strategy?.common.initial_amount
                      ? `${formatMoney(strategy.common.initial_amount, false)} to be paid upfront`
                      : undefined
                  }
                />
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}>
                <StatCard
                  label="Total Debt"
                  text={formatMoney(strategy?.common.total_debt, true)}
                  subtext={
                    Math.max(...(strategy?.common.split_rows.creditor_max_weekly ?? []))
                      ? `${formatMoney(Safe(Math.max(...(strategy?.common.split_rows.creditor_max_weekly ?? []))).multiply(frequency === "month" ? 4 : 1).value, false)} / ${frequency}`
                      : "One-Time payment"
                  }
                />
              </Col>
              <Col span={12}>
                <StatCard
                  label="Net Commission"
                  text={formatMoney(strategy?.common.interest, true)}
                  subtext={
                    strategy?.common.split_rows.program_weekly
                      ? `${formatMoney(Safe(strategy?.common.split_rows.program_weekly).multiply(frequency === "month" ? 4 : 1).value, false)} / ${frequency}`
                      : "One-Time payment"
                  }
                />
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}>
                <StatCard
                  label="Tenure"
                  text={`${strategy?.payment_type === PaymentType.MONTHLY ? Math.floor(strategy.total_weeks / 4) : strategy?.total_weeks} ${frequency}s`}
                />
              </Col>
              <Col span={12}>
                <StatCard
                  label="Program Tenure"
                  text={`${strategy?.payment_type === PaymentType.MONTHLY ? Math.floor(strategy.program_tenure / 4) : strategy?.program_tenure} ${frequency}s`}
                />
              </Col>
            </Row>
          </Flex>
        </Section>
        <Section bordered title="Payment Breakdown">
          <PaymentBreakdown
            total={payment_table.totals.payment}
            commission={payment_table.totals.program_fee}
            creditor={payment_table.totals.creditor}
            service={payment_table.totals.service_fee}
            legal={payment_table.totals.legal_fee}
          />
        </Section>
        {strategy.total_weeks > 0 && (
          <Section bordered title="Weekly Payment Timeline">
            <WeeklyPaymentTimeline
              ledgerEntries={ledger}
              initial_weeks={strategy.common.initial_split ?? 1}
              program_weeks={strategy.program_tenure}
            />
          </Section>
        )}
        <Section
          title="Payment Ledger"
          extra={
            <Space>
              <Button
                size="small"
                disabled={ledger.length === 0}
                onClick={() => setPaymentTableShowMode((prev) => (prev === "compact" ? "detailed" : "compact"))}
              >
                {paymentTableShowMode === "detailed" ? "Compact" : "Detailed"}
              </Button>
              <Select
                mode="multiple"
                value={visibleHeaders}
                onChange={setVisibleHeaders}
                options={defaultHeaders.map((item) => ({
                  label: toCapitalCase(item),
                  key: item,
                  value: item,
                }))}
                maxTagCount={0}
                tagRender={() => <div />}
                suffixIcon={<TbFilter className="text-black" />}
                size="small"
                className="min-w-8 w-fit h-fit !p-0"
                styles={{
                  popup: {
                    root: {
                      minWidth: 220,
                    },
                  },
                }}
              />
            </Space>
          }
        >
          <LedgerTable
            ledgerRows={ledger ?? []}
            paymentTable={payment_table}
            mode={paymentTableShowMode}
            headers={visibleHeaders}
            cycle={strategy?.payment_type === PaymentType.MONTHLY ? "monthly" : "weekly"}
            initialPaymentRange={strategy?.common.initial_split ?? 1}
            interestPaymentRange={strategy?.program_tenure}
          />
        </Section>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-12 gap-4 items-start">
      <div className="col-span-6 max-h-[85vh] overflow-y-auto">
        {hasSplit && (
          <Alert
            type="info"
            showIcon
            message="Commission split inferred"
            description="We could not find the program and success part split for this strategy, so the net commission was split evenly. Please adjust if needed."
            className="mb-4"
          />
        )}

        <Flex vertical className="px-2">
          <Row>
            <Col span={24}>
              <Form.Item name={[name, "key"]} label="Title" rules={[{ required: true, min: 1 }]}>
                <Input placeholder="Standard Program v1" />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Form.Item name={[name, "description"]} label="Description">
                <Input.TextArea autoSize={{ minRows: 1, maxRows: 1 }} placeholder="Optional strategy details..." />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Form.Item name={[name, "template_id"]} label="Pandadoc Template">
                <Select
                  style={{ width: "100%" }}
                  placeholder="Select PandaDoc template for this strategy"
                  options={pandaDocConfig.map((config) => ({
                    label: config.title,
                    value: config._id,
                  }))}
                  popupRender={(menu) => (
                    <>
                      {menu}

                      <Divider style={{ margin: "8px 0" }} />

                      <div style={{ padding: 8 }}>
                        <Button
                          type="primary"
                          block
                          onClick={() =>
                            window.open(
                              "/dashboard/application/key-mapping?org_code=" + application.organisation?.code,
                              "_blank",
                            )
                          }
                        >
                          + Create New Template
                        </Button>
                      </div>
                    </>
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name={[name, "resolution_type"]}
                label={
                  <Space>
                    <TbTargetArrow /> Type
                  </Space>
                }
              >
                <Select
                  options={Object.values(ResolutionType).map((v) => ({
                    label: toTitleCase(v.replace("-", "_"), { sep: "_", forceLower: true }),
                    value: v,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={[name, "format"]}
                label={
                  <Space>
                    <TbSettingsAutomation /> Format
                  </Space>
                }
              >
                <Select
                  options={Object.values(StrategyFormat).map((v) => ({
                    label: toTitleCase(v.replace("-", "_"), { sep: "_", forceLower: true }),
                    value: v,
                    disabled: v !== StrategyFormat.MANAGERS_APPROVAL,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name={[name, "payment_type"]}
                label={
                  <Space>
                    <TbCalendarRepeat /> Payment
                  </Space>
                }
              >
                <Select
                  options={Object.values(PaymentType).map((v) => ({
                    label: toTitleCase(v.replace("-", "_"), { sep: "_", forceLower: true }),
                    value: v,
                  }))}
                  onChange={(value) => {
                    if (value === PaymentType.MONTHLY) {
                      form.setFieldValue(
                        ["strategies", name, "target_weekly_calculation_strategy"],
                        TargetWeeklyCalculationStrategy.COMPACT,
                      );
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={[name, "target_weekly_calculation_strategy"]}
                label={
                  <Space>
                    <TbMathFunction /> Week Calculation
                  </Space>
                }
              >
                <Select
                  options={Object.values(TargetWeeklyCalculationStrategy).map((v) => ({
                    label: toTitleCase(v, { sep: "_", forceLower: true }),
                    value: v,
                    disabled: v === TargetWeeklyCalculationStrategy.LOW_WEEKLY,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
        </Flex>

        <Collapse ghost defaultActiveKey={["fees"]}>
          <Panel
            forceRender
            header={
              <Space>
                <TbTableOptions /> Fees & Financials
              </Space>
            }
            key="fees"
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label={
                    <Space>
                      <TbFlagCheck /> Initial Fee (%)
                    </Space>
                  }
                >
                  <Row gutter={8}>
                    <Col span={12}>
                      <Form.Item name={[name, "initial_fee"]}>
                        <InputNumber {...pctProps} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={[name, "initial_fee_base"]} noStyle>
                        <Button
                          className="w-full"
                          onClick={() =>
                            form.setFieldValue(
                              ["strategies", name, "initial_fee_base"],
                              initial_fee_base === InitialPaymentPercentageOf.TOTAL_INTEREST
                                ? InitialPaymentPercentageOf.TOTAL_DEBT
                                : InitialPaymentPercentageOf.TOTAL_INTEREST,
                            )
                          }
                          type="primary"
                          variant="solid"
                          color={initial_fee_base === InitialPaymentPercentageOf.TOTAL_INTEREST ? "geekblue" : "purple"}
                        >
                          of {initial_fee_base === InitialPaymentPercentageOf.TOTAL_INTEREST ? "Interest" : "Debt"}
                        </Button>
                      </Form.Item>
                    </Col>
                  </Row>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={
                    <Space>
                      <TbScale /> Upfront Amount
                    </Space>
                  }
                >
                  <Row gutter={8}>
                    <Col span={12}>
                      <Form.Item name="is_bias_exclusive" noStyle>
                        <Button
                          className="w-full"
                          onClick={() =>
                            form.setFieldValue(["strategies", name, "is_bias_exclusive"], !is_bias_exclusive)
                          }
                          // icon={pbToggle ? <TbArrowUp /> : <TbArrowDown />}
                          type="primary"
                          variant="solid"
                          color={is_bias_exclusive ? "orange" : "cyan"}
                        >
                          {is_bias_exclusive ? "Exclusive" : "Inclusive"}
                        </Button>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={[name, "initial_bias_amount"]}>
                        <InputNumber prefix="$" className="w-full" min={0} />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name={[name, "interest_calculation_strategy"]}
                  label={
                    <Space>
                      <TbPercentage /> Interest Calculation
                    </Space>
                  }
                >
                  <Select
                    options={Object.values(InterestCalculationStrategy).map((v) => ({
                      label: toTitleCase(v, { sep: "_", forceLower: true }),
                      value: v,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={
                    <Space>
                      <TbChevronsRight /> Dynamic Pushback (%)
                    </Space>
                  }
                >
                  <Row gutter={8}>
                    <Col span={16}>
                      <Button
                        className="w-full"
                        onClick={() =>
                          form.setFieldValue(
                            ["strategies", name, "dynamic_weekly_pushback"],
                            pbToggle ? -Math.abs(dynamic_weekly_pushback) : Math.abs(dynamic_weekly_pushback),
                          )
                        }
                        icon={pbToggle ? <TbArrowUp /> : <TbArrowDown />}
                        type="primary"
                        variant="solid"
                        color={pbToggle ? "green" : "red"}
                      >
                        {pbToggle ? "Increase" : "Decrease"}
                      </Button>
                    </Col>
                    <Col span={8}>
                      <Form.Item name={[name, "dynamic_weekly_pushback"]} noStyle>
                        <InputNumber {...pctProps} min={-1} />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form.Item>
              </Col>
            </Row>
            {interestStrategy === InterestCalculationStrategy.PERCENTAGE ? (
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name={[name, "program_fee"]}
                    label={
                      <Space>
                        <TbReceiptTax /> Program Fee (%)
                      </Space>
                    }
                  >
                    <InputNumber {...pctProps} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name={[name, "success_fee"]}
                    label={
                      <Space>
                        <TbTrophy /> Success Fee (%)
                      </Space>
                    }
                  >
                    <InputNumber {...pctProps} />
                  </Form.Item>
                </Col>
              </Row>
            ) : (
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name={[name, "interest_amount"]}
                    label={
                      <Space>
                        <TbReceiptTax /> Interest Amount
                      </Space>
                    }
                  >
                    <InputNumber prefix="$" className="w-full" min={0} />
                  </Form.Item>
                </Col>
              </Row>
            )}
          </Panel>

          <Panel
            forceRender
            header={
              <Space>
                <TbColumns /> Program Split
              </Space>
            }
            key="program"
          >
            <div>
              <div className="grid grid-cols-2 gap-4">
                <Form.Item name={[name, "program_split", "format"]} label="Split Format">
                  <Select
                    options={Object.values(ProgramSplitFormat)
                      .filter((format) => format !== ProgramSplitFormat.PERCENTAGE_OF_REVENUE)
                      .map((v) => ({
                        label: toTitleCase(v, { sep: "_", forceLower: true }),
                        value: v,
                      }))}
                  />
                </Form.Item>
                <Form.Item
                  name={[name, "program_split", "initial_payment_timeline"]}
                  label={
                    <Space.Compact>
                      Initial Payment Timeline
                      <Tooltip title="Initial payment will be divided in x + 1 weeks">
                        <TbInfoCircle className="ml-2" />
                      </Tooltip>
                    </Space.Compact>
                  }
                >
                  <InputNumber className="w-full" min={0} />
                </Form.Item>
              </div>
            </div>
            {[ProgramSplitFormat.EARLY_STOPPING_WEEKS].includes(splitFormat) && (
              <Flex className="justify-between">
                <Form.Item
                  name={[name, "program_split", "meta", "base"]}
                  initialValue={defaultStrategyValue.program_split?.meta?.base ?? "debt"}
                  className="mb-3 w-48"
                  label="Base"
                >
                  <Select
                    size="small"
                    options={[
                      { label: "Total Debt", value: "debt" },
                      { label: "Total Interest", value: "interest" },
                    ]}
                    onChange={(value) => {
                      const multiplier = value === "interest" ? 0.25 : 4;
                      const steps = form.getFieldValue(["strategies", name, "program_split", "meta", "steps"]);
                      const modified = steps.map((step: Partial<{ debt_cap: number; weeks: number }>) => ({
                        debt_cap: Math.ceil((step.debt_cap ?? 0) * multiplier),
                        weeks: Math.ceil((step.weeks ?? 0) * multiplier),
                      }));

                      form.setFieldValue(["strategies", name, "program_split", "meta", "steps"], modified);
                    }}
                  />
                </Form.Item>
                {splitFormat === ProgramSplitFormat.EARLY_STOPPING_WEEKS ? (
                  <Form.Item
                    name={[name, "program_split", "meta", "mode"]}
                    initialValue={defaultStrategyValue.program_split?.meta?.mode ?? "variable"}
                    className="mb-3"
                    label="Mode"
                  >
                    <Segmented
                      options={[
                        { label: "Fixed", value: "fixed" },
                        { label: "Variable", value: "variable" },
                      ]}
                    />
                  </Form.Item>
                ) : null}
              </Flex>
            )}
            {splitFormat === ProgramSplitFormat.EARLY_STOPPING_WEEKS ? (
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) => {
                  return (
                    prev.strategies?.[name]?.program_split?.meta?.mode !==
                      curr.strategies?.[name]?.program_split?.meta?.mode ||
                    prev.strategies?.[name]?.program_split?.meta?.steps !==
                      curr.strategies?.[name]?.program_split?.meta?.steps
                  );
                }}
              >
                {({ getFieldValue }) => {
                  const mode = getFieldValue(["strategies", name, "program_split", "meta", "mode"]) ?? "variable";

                  if (mode === "fixed") {
                    return (
                      <div className="bg-gray-50 flex items-start gap-4 rounded">
                        <Form.Item
                          name={[name, "program_split", "meta", "fixed_weeks"]}
                          label="All debt ranges"
                          rules={[{ required: true }]}
                          className="w-1/2"
                        >
                          <InputNumber suffix="weeks" min={1} className="w-full" />
                        </Form.Item>
                      </div>
                    );
                  }

                  return (
                    <Form.List
                      name={[name, "program_split", "meta", "steps"]}
                      initialValue={(defaultStrategyValue.program_split.meta?.steps ?? []) as unknown[]}
                    >
                      {(fields) => {
                        const liveSteps =
                          getFieldValue(["strategies", name, "program_split", "meta", "steps"]) ??
                          defaultStrategyValue.program_split?.meta?.steps ??
                          [];

                        const items: FormListFieldData[] =
                          fields.length > 0
                            ? fields
                            : liveSteps.map((_: unknown, i: number) => ({
                                key: i,
                                name: i,
                                fieldKey: i,
                                isListField: true,
                              }));

                        return (
                          <div className="bg-gray-50 flex flex-wrap items-start gap-2">
                            {items.map(({ key: itemKey, name: fieldName, ...restField }) => {
                              const isLast = liveSteps.length - 1 === itemKey;

                              const threshold = isLast
                                ? (liveSteps[itemKey - 1]?.debt_cap ?? 0)
                                : (liveSteps[itemKey]?.debt_cap ?? 0);

                              return (
                                <div key={itemKey} className="flex flex-col items-start">
                                  <Form.Item
                                    {...restField}
                                    name={[fieldName, "weeks"]}
                                    label={`${toCapitalCase(base)} ${isLast ? ">" : "≤"} ${formatMoney(threshold)}`}
                                    rules={[{ required: true }]}
                                  >
                                    <InputNumber style={{ width: 150 }} suffix="weeks" min={1} />
                                  </Form.Item>
                                </div>
                              );
                            })}
                          </div>
                        );
                      }}
                    </Form.List>
                  );
                }}
              </Form.Item>
            ) : (
              <Form.Item
                hidden={["unified", "unknown"].includes(getProgramSplitTitle(splitFormat).toLocaleLowerCase())}
                name={[name, "program_split", "meta", "percentage"]}
                label={getProgramSplitTitle(splitFormat)}
              >
                <InputNumber
                  disabled={["unified", "unknown"].includes(getProgramSplitTitle(splitFormat).toLocaleLowerCase())}
                  className="w-full"
                  min={0}
                />
              </Form.Item>
            )}
          </Panel>

          <Panel
            forceRender
            header={
              <Space>
                <TbUsersGroup /> Creditor Settings
              </Space>
            }
            key="creditor"
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name={[name, "creditor_split", "total_payback"]} label="Payback Ratio">
                  <InputNumber {...pctProps} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name={[name, "creditor_split", "initial_bias_amount"]} label="Creditor Upfront Amount">
                  <InputNumber prefix="$" className="w-full" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name={[name, "creditor_split", "format"]} label="Amount Format">
                  <Select
                    options={Object.values(CreditorSplitFormat).map((v) => ({
                      label: toTitleCase(v, { sep: "_", forceLower: true }),
                      value: v,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name={[name, "creditor_split", "pay_format"]} label="Pay Via">
                  <Select
                    options={Object.values(CreditorPayFormat).map((v) => ({
                      label: toTitleCase(v, { sep: "_", forceLower: true }),
                      value: v,
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Panel>

          <Panel
            forceRender
            header={
              <Space>
                <TbEye /> Display Timeline Rules
              </Space>
            }
            key="timeline"
          >
            <Col span={12}>
              <Form.Item
                name={[name, "fallback_display_timeline"]}
                label={<Space>Display Timeline Multiplier (%)</Space>}
              >
                <InputNumber {...pctProps} />
              </Form.Item>
            </Col>
          </Panel>

          <Panel
            forceRender
            header={
              <Space>
                <TbMathFunction /> Custom Parameters
              </Space>
            }
            key="custom"
          >
            <Flex gap={8}>
              {!customizeParameters.includes("service_fee") && (
                <Button
                  size="small"
                  variant="outlined"
                  color="orange"
                  onClick={() => setCustomizeParameters((prev) => [...prev, "service_fee"])}
                >
                  Modify Service Fee
                </Button>
              )}
              {!customizeParameters.includes("legal_fee") && (
                <Button
                  size="small"
                  variant="outlined"
                  color="red"
                  onClick={() => setCustomizeParameters((prev) => [...prev, "legal_fee"])}
                >
                  Modify Legal Fee
                </Button>
              )}
            </Flex>

            <Row className="my-4" gutter={[16, 16]}>
              {customizeParameters.map((param) => (
                <ModifyParameters
                  key={`customize_param_${param}`}
                  name={[name, param]}
                  slug={param}
                  collapse={false}
                  onRemove={() => {
                    form.setFieldValue(["strategies", name, param], undefined);
                    setCustomizeParameters((prev) => prev.filter((k) => k !== param));
                  }}
                />
              ))}
            </Row>
          </Panel>

          <Panel
            forceRender
            header={
              <Space>
                <TbFileText /> Rules
              </Space>
            }
            key="rules"
          >
            <Form.List name={[name, "rules"]}>
              {(fields, { add, remove }) => {
                const rules = form.getFieldValue(["strategies", name, "rules"]) ?? [];

                const usedKeys = new Set(rules.map((r: any) => r?.key).filter(Boolean));

                const allRuleOptions = Object.entries(StrategyRuleKeys).map(([label, value]) => ({
                  label,
                  value,
                }));

                const availableRuleOptions = allRuleOptions.filter(({ value }) => !usedKeys.has(value));

                return (
                  <>
                    {fields.map(({ key: fieldKey, name: fieldName, ...restField }) => {
                      const currentKey = rules[fieldName]?.key;
                      const rowOptions = allRuleOptions.filter(
                        ({ value }) => !usedKeys.has(value) || value === currentKey,
                      );

                      return (
                        <Row key={fieldKey} gutter={16} align="middle">
                          <Col span={8}>
                            <Form.Item
                              {...restField}
                              name={[fieldName, "key"]}
                              label="Rule"
                              rules={[{ required: true, message: "Key is required" }]}
                            >
                              <Select
                                options={rowOptions}
                                popupMatchSelectWidth={240}
                                placeholder="e.g. Max Program Tenure"
                              />
                            </Form.Item>
                          </Col>
                          <Col span={14}>
                            <Form.Item
                              {...restField}
                              name={[fieldName, "value"]}
                              label="Value"
                              rules={[{ required: true, message: "Value is required" }]}
                            >
                              <InputNumber className="w-full" />
                            </Form.Item>
                          </Col>
                          <Col span={2}>
                            <Button type="text" danger icon={<TbTrash />} onClick={() => remove(fieldName)} />
                          </Col>
                        </Row>
                      );
                    })}
                    <Form.Item>
                      <Button
                        type="dashed"
                        disabled={availableRuleOptions.length === 0}
                        onClick={() =>
                          availableRuleOptions.length > 0 &&
                          add({
                            key: availableRuleOptions[0].value,
                            value: 0,
                          })
                        }
                        block
                        icon={<TbPlus />}
                      >
                        Add Rule
                      </Button>
                    </Form.Item>
                  </>
                );
              }}
            </Form.List>
          </Panel>
        </Collapse>
      </div>
      <Preview
        index={name}
        title={key}
        slug={slug}
        resolution_type={resolution_type}
        format={format}
        payment_type={payment_type}
        program_fee={program_fee}
        success_fee={success_fee}
        initial_fee={initial_fee}
        initial_fee_base={initial_fee_base}
        initial_bias_amount={initial_bias_amount}
        is_bias_exclusive={is_bias_exclusive}
        dynamic_weekly_pushback={dynamic_weekly_pushback}
        target_weekly_calculation_strategy={target_weekly_calculation_strategy}
        program_split={program_split}
        creditor_split={creditor_split}
        description={description}
        fallback_display_timeline={fallback_display_timeline}
        display_timeline={display_timeline}
        meta={meta}
        setMetaFields={(field, value) => form.setFieldValue(["strategies", name, "meta", field], value)}
        interest_calculation_strategy={interestStrategy || InterestCalculationStrategy.PERCENTAGE}
        interest_amount={interest_amount ?? 0}
        rules={rules}
        service_fee={service_fee}
        legal_fee={legal_fee}
      />
    </div>
  );
};

export default StrategyPanel;
